import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  Skeleton,
  Stack,
  Divider,
  Paper,
  InputBase,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PlaceIcon from "@mui/icons-material/Place";
import CategoryIcon from "@mui/icons-material/Category";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { motion } from "framer-motion";
import EmptyState from "../../shared/ui/EmptyState";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CardSkeleton = () => (
  <Card
    elevation={0}
    sx={{ borderRadius: 2, overflow: "hidden", border: "1px solid", borderColor: "divider" }}
  >
    <Box sx={{ position: "relative", paddingTop: "66.67%", bgcolor: "action.hover" }}>
      <Skeleton variant="rectangular" sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }} />
    </Box>
    <CardContent sx={{ p: 2 }}>
      <Skeleton variant="text" width="85%" height={20} sx={{ mb: 0.5 }} />
      <Skeleton variant="text" width="55%" height={16} sx={{ mb: 1.5 }} />
      <Divider sx={{ mb: 1.5 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Skeleton variant="text" width={90} height={28} />
        <Skeleton variant="rounded" width={80} height={32} />
      </Stack>
    </CardContent>
  </Card>
);

const DistributorPublicPage = () => {
  const { distributorId } = useParams();
  const navigate = useNavigate();

  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");

  useEffect(() => {
    const fetchTours = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (location) params.set("location", location);
        if (category) params.set("category", category);

        const res = await fetch(
          `${API_BASE}/api/widget/${distributorId}?${params.toString()}`
        );
        if (!res.ok) throw new Error("Failed to load tours");
        const data = await res.json();
        setTours(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Something went wrong");
        setTours([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [distributorId, search, location, category]);

  const hasFilters = search || location || category;
  const clearFilters = () => { setSearch(""); setLocation(""); setCategory(""); };

  return (
    <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 6 },
          py: 3,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <ExploreOutlinedIcon sx={{ color: "#E50000", fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: -0.3, lineHeight: 1.2 }}>
              Tour Experiences
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {loading
                ? "Loading available tours…"
                : tours.length > 0
                ? `${tours.length} experience${tours.length !== 1 ? "s" : ""} available`
                : hasFilters
                ? "No tours match your filters"
                : "No tours available yet"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Filter bar */}
      <Box
        sx={{
          bgcolor: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2, md: 6 },
          py: 2,
          position: "sticky",
          top: 0,
          zIndex: 100,
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)",
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              flex: 1,
              maxWidth: { sm: 380 },
              border: "1.5px solid",
              borderColor: "divider",
              borderRadius: 99,
              px: 2,
              py: 0.6,
              "&:focus-within": { borderColor: "#E50000" },
            }}
          >
            <SearchIcon sx={{ color: "text.disabled", fontSize: 19, mr: 1, flexShrink: 0 }} />
            <InputBase
              placeholder="Search tours…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1, fontSize: "0.875rem" }}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.25, ml: 0.5 }}>
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            )}
          </Paper>

          <TextField
            size="small"
            placeholder="Location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceIcon sx={{ fontSize: 16, color: location ? "#E50000" : "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: location ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setLocation("")} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 99,
                fontSize: "0.85rem",
                ...(location && { "& fieldset": { borderColor: "#E50000" } }),
              },
            }}
          />

          <TextField
            size="small"
            placeholder="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CategoryIcon sx={{ fontSize: 16, color: category ? "#E50000" : "text.disabled" }} />
                </InputAdornment>
              ),
              endAdornment: category ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setCategory("")} sx={{ p: 0.25 }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              minWidth: 145,
              "& .MuiOutlinedInput-root": {
                borderRadius: 99,
                fontSize: "0.85rem",
                ...(category && { "& fieldset": { borderColor: "#E50000" } }),
              },
            }}
          />

          {hasFilters && (
            <Button
              variant="text"
              size="small"
              onClick={clearFilters}
              sx={{ color: "text.secondary", fontSize: "0.8rem", whiteSpace: "nowrap", minWidth: "auto" }}
            >
              Clear all
            </Button>
          )}
        </Stack>
      </Box>

      {/* Tour grid */}
      <Box sx={{ px: { xs: 2, md: 6 }, py: 4 }}>
        {error && (
          <Box sx={{ py: 8 }}>
            <EmptyState
              icon={<ExploreOutlinedIcon />}
              title="Couldn't load tours"
              description={error}
            />
          </Box>
        )}

        {!error && loading && (
          <Grid container spacing={2.5}>
            {Array.from({ length: 6 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <CardSkeleton />
              </Grid>
            ))}
          </Grid>
        )}

        {!error && !loading && tours.length === 0 && (
          <Box sx={{ py: 8 }}>
            <EmptyState
              icon={hasFilters ? <SearchOffIcon /> : <ExploreOutlinedIcon />}
              title={hasFilters ? "No results found" : "No tours available"}
              description={
                hasFilters
                  ? "Try different search terms or clear your filters."
                  : "Check back soon for available tour packages."
              }
              action={
                hasFilters ? (
                  <Button variant="outlined" onClick={clearFilters}>Clear filters</Button>
                ) : undefined
              }
            />
          </Box>
        )}

        {!error && !loading && tours.length > 0 && (
          <Grid container spacing={2.5}>
            {tours.map((tour, index) => (
              <Grid item xs={12} sm={6} md={4} key={tour.id} sx={{ display: "flex" }}>
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.04 }}
                  style={{ display: "flex", flex: 1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "white",
                      transition: "box-shadow 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        boxShadow: "0 6px 24px rgba(0,0,0,0.1)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >
                    {/* Image */}
                    <Box sx={{ position: "relative", paddingTop: "66.67%", bgcolor: "#f0f2f5" }}>
                      {tour.image ? (
                        <Box
                          component="img"
                          src={tour.image}
                          alt={tour.title}
                          onError={(e) => { e.target.style.display = "none"; }}
                          sx={{
                            position: "absolute",
                            top: 0, left: 0,
                            width: "100%", height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ExploreOutlinedIcon sx={{ fontSize: 52, color: "rgba(0,0,0,0.12)" }} />
                        </Box>
                      )}

                      {tour.category && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 10, left: 10,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "white",
                            px: 1.25, py: 0.35,
                            borderRadius: 1,
                            fontSize: "0.68rem",
                            fontWeight: 700,
                            backdropFilter: "blur(6px)",
                            letterSpacing: 0.3,
                            textTransform: "uppercase",
                          }}
                        >
                          {tour.category}
                        </Box>
                      )}
                    </Box>

                    {/* Content */}
                    <CardContent
                      sx={{
                        p: 2,
                        pb: "16px !important",
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: "0.93rem",
                          lineHeight: 1.4,
                          mb: 0.5,
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {tour.title}
                      </Typography>

                      <Stack direction="row" spacing={2} sx={{ mb: "auto" }} flexWrap="wrap">
                        {(tour.location?.city || tour.location?.state || tour.location?.country) && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <PlaceIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 150 }}>
                              {[tour.location.city, tour.location.country].filter(Boolean).join(", ")}
                            </Typography>
                          </Stack>
                        )}
                        {tour.availability?.length > 0 && (
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <AccessTimeIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                            <Typography variant="caption" color="text.secondary">
                              {tour.availability.length} date{tour.availability.length !== 1 ? "s" : ""} available
                            </Typography>
                          </Stack>
                        )}
                      </Stack>

                      <Box
                        sx={{
                          mt: 2,
                          pt: 1.5,
                          borderTop: "1px solid",
                          borderColor: "divider",
                          display: "flex",
                          alignItems: "flex-end",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", lineHeight: 1.2 }}>
                            From
                          </Typography>
                          <Typography fontWeight={800} sx={{ fontSize: "1rem", lineHeight: 1.2, color: "text.primary" }}>
                            {tour.currency} {Number(tour.price).toLocaleString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            per person
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          disableElevation
                          onClick={() =>
                            navigate(`/book/${tour.id}?distributor=${distributorId}`, {
                              state: { tour },
                            })
                          }
                          sx={{
                            bgcolor: "#E50000",
                            "&:hover": { bgcolor: "#C00000" },
                            borderRadius: 1.5,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            px: 2,
                            py: 0.75,
                            flexShrink: 0,
                          }}
                        >
                          Book Now
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Box>
  );
};

export default DistributorPublicPage;
